import { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import Button from '@/components/Button';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';

import { useBroadcastRecipientList } from '../api/useBroadcastRecipientList';
import type { WorkerChip } from '../api/useWorkersByIds';
import type { Broadcast } from '../types';

interface BroadcastRecipientsProps {
  broadcast: Broadcast;
}

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none',
  margin: '0 0 12px',
  boxSizing: 'border-box',
};

const metaStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  borderBottom: '1px solid #f3f4f6',
};

const rowInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 1,
  minWidth: 0,
};

const nameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#111827',
};

const profileLinkStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
  color: '#6b7280',
};

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  padding: '8px 10px',
  borderRadius: 6,
  background: '#fef2f2',
  color: '#b91c1c',
  marginBottom: 12,
};

const loadMoreButtonStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  fontSize: 12,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 6,
  margin: '8px 10px',
};

const scrollContainerStyle: React.CSSProperties = {
  maxHeight: '60vh',
  overflowY: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};

function workerName(
  worker: WorkerChip | null | undefined,
  fallback: string,
): string {
  const first = worker?.user?.first_name ?? '';
  const last = worker?.user?.last_name ?? '';
  const composed = `${first} ${last}`.trim();
  return composed || worker?.user?.email || fallback;
}

function RecipientRow({
  id,
  worker,
}: {
  id: number;
  worker: WorkerChip | null | undefined;
}) {
  return (
    <div style={rowStyle}>
      <div style={rowInfoStyle}>
        <div style={nameStyle}>{workerName(worker, `Worker #${id}`)}</div>
        <div style={metaStyle}>{worker?.user?.email ?? ''}</div>
      </div>
      <a
        href={`/workers_v2/${id}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open worker profile"
        data-testid="recipientProfileLink"
        onClick={(e) => e.stopPropagation()}
        style={profileLinkStyle}
      >
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </a>
    </div>
  );
}

export function BroadcastRecipients({ broadcast }: BroadcastRecipientsProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const trimmedQuery = searchQuery.trim();
  const isQueryActive = trimmedQuery.length >= 2;

  // One endpoint serves both the plain list and the search — it scopes the
  // search to this broadcast's recipients server-side, so a match is found
  // regardless of how many workers platform-wide share the name.
  const { recipients, total, loading, error, hasMore, loadMore } =
    useBroadcastRecipientList(
      broadcast.id,
      isQueryActive ? trimmedQuery : '',
      open,
    );

  // Prefer the persisted count: it is the delivered total, needs no query,
  // and stays correct while the list below is filtered by a search.
  const displayCount = broadcast.recipient_count ?? total;
  const titleText = `Recipients${displayCount != null ? ` (${displayCount})` : ''}`;

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
  }, []);

  const noMatches =
    isQueryActive && !loading && !error && recipients.length === 0;

  return (
    <>
      <Button
        a11yLabel="View workers"
        appearance="outline"
        label="View workers"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      />

      {open ? (
        <Modal
          displayFlex
          size="sm"
          title={titleText}
          onRequestClose={handleClose}
          wrapperBackground
        >
          <Card>
            <Card.Section>
              <div data-testid="broadcastRecipients">
                <input
                  type="search"
                  data-testid="recipientSearchInput"
                  placeholder="Search all recipients by name or email…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={searchInputStyle}
                  autoFocus
                />

                {!isQueryActive ? (
                  <div style={{ ...metaStyle, marginBottom: 8 }}>
                    {displayCount != null
                      ? `${recipients.length} of ${displayCount} loaded`
                      : `${recipients.length} loaded`}
                  </div>
                ) : null}

                <div style={scrollContainerStyle}>
                  {loading && recipients.length === 0 ? (
                    <div style={{ padding: 12 }}>
                      {isQueryActive ? (
                        <span style={metaStyle}>Searching…</span>
                      ) : (
                        <LoadingState />
                      )}
                    </div>
                  ) : error && recipients.length === 0 ? (
                    <div style={{ ...errorStyle, margin: 10 }}>{error}</div>
                  ) : noMatches ? (
                    <div style={{ padding: '12px 10px', ...metaStyle }}>
                      No recipients match
                    </div>
                  ) : recipients.length === 0 ? (
                    <div style={{ padding: '12px 10px', ...metaStyle }}>
                      No recipients yet.
                    </div>
                  ) : (
                    <>
                      {recipients.map((worker) => (
                        <RecipientRow
                          key={worker.id}
                          id={worker.id}
                          worker={worker}
                        />
                      ))}
                      {error ? (
                        <div style={{ ...errorStyle, margin: 10 }}>{error}</div>
                      ) : null}
                    </>
                  )}

                  {hasMore ? (
                    <button
                      type="button"
                      onClick={() => void loadMore()}
                      disabled={loading}
                      style={{
                        ...loadMoreButtonStyle,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? 'wait' : 'pointer',
                      }}
                    >
                      {loading ? 'Loading…' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              </div>
            </Card.Section>
          </Card>
        </Modal>
      ) : null}
    </>
  );
}

export default BroadcastRecipients;
